// Members, for a program for roles (any category): the last step of the build flow. The
// program is for the roles chosen on Details and for those roles only, so there
// is nothing to choose here: no audience to edit, and nobody invited by name or
// in bulk. It shows the roles, who holds them today, and Invite & Finish.

import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Typography } from '@mui/material';
import { API } from '../../../../../api';
import { apiMessage, capabilityApi } from '../../../../../api/capability';
import { useCapabilityOrg } from '../../../../../queries/useCapability';
import { getLLAuthHeaders } from '../../../utils/llAuthHeaders';
import { Bone } from '../../../../../components/ui/DashboardSkeletons';
import type { SetupRole } from '../capabilitySetup';
import {
  ActionButton,
  BODY,
  BRANDY_FILL,
  BRANDY_FRAME,
  FONT,
  FlowStep,
  FormRow,
  LABEL,
  LINE,
  Note,
  TEXT,
  TEXT_3
} from '../designSystem';
import { roleAudienceReady } from '../flags';

interface Person {
  id: string;
  name: string;
  roleName: string | null;
}

const RoleMembersStep: React.FC<{
  groupId: string;
  /** The program's name, for the toast once it is published. */
  programName?: string;
  /** The roles the program is for (those of them still set up). */
  roles: SetupRole[];
  /** Their names as kept with the program, for any role no longer set up. */
  roleNames: string[];
  stepper: React.ReactNode;
  /** Invite & Finish: the program is live. */
  onFinished: () => void;
}> = ({ groupId, programName, roles, roleNames, stepper, onFinished }) => {
  const org = useCapabilityOrg();
  const names = roles.length > 0 ? roles.map((r) => r.name) : roleNames;
  const roleKey = roles.map((r) => r.id).join('|');

  // Who holds these roles today, read team by team.
  const [people, setPeople] = useState<Person[] | null>(null);
  useEffect(() => {
    let alive = true;
    const roleIds = new Set(roles.map((r) => r.id));
    const teamIds = Array.from(new Set(roles.map((r) => r.teamId)));
    Promise.all(teamIds.map((teamId) => capabilityApi.teamMembers(teamId, org)))
      .then((teams) => {
        if (!alive) return;
        const holders = teams.flatMap((t) => t.people).filter((p) => p.roleId && roleIds.has(p.roleId));
        setPeople(holders.sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => alive && setPeople([]));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleKey, org]);

  const [finishing, setFinishing] = useState(false);
  const ready = roleAudienceReady();
  const finish = async () => {
    setFinishing(true);
    try {
      // On a program for roles: enrols the people who hold them, marks it Invited (even with nobody
      // holding them yet), and from then on enrols whoever takes one of them (see flags.ts).
      await API.enrollProgramAudience({ id: groupId, ...getLLAuthHeaders() });
      toast.success(`${programName || 'The program'} is published.`);
      onFinished();
    } catch (err) {
      toast.error(apiMessage(err, 'The program could not be published. Please try again.'));
    } finally {
      setFinishing(false);
    }
  };

  const count = people?.length ?? 0;
  const title =
    people === null
      ? 'Finding who holds these roles'
      : `${count} ${count === 1 ? 'person holds' : 'people hold'} ${names.length === 1 ? 'this role' : 'these roles'}`;
  const body = ready
    ? 'They are enrolled when you click Enroll People. Anyone who takes one of these roles later is enrolled automatically.'
    : 'Enrolling people by their role is not connected yet. For now, Enroll People publishes the program without enrolling them.';

  return (
    <FlowStep
      stepper={stepper}
      bar={{
        done: people !== null,
        title,
        body,
        actions: (
          <ActionButton primary disabled={finishing} reason="One moment." onClick={finish}>
            {finishing ? 'Enrolling...' : 'Enroll People'}
          </ActionButton>
        )
      }}
    >
      <FormRow label="Audience" tip="Chosen on Details when the program was built.">
        {/* Shown, not chosen: nothing here can be ticked or unticked. */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {names.map((name) => (
            <Box
              key={name}
              component="span"
              sx={{
                height: 32,
                px: '12px',
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: '4px',
                border: BRANDY_FRAME,
                bgcolor: BRANDY_FILL,
                color: TEXT,
                fontFamily: FONT,
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
            >
              {name}
            </Box>
          ))}
        </Box>
        <Note>This program is for the roles chosen on Details.</Note>
      </FormRow>
      <FormRow
        label="People"
        tip="Everyone who holds one of these roles today. Roles are given to people in Operations."
        aside={people !== null && <Typography sx={{ ...LABEL, color: TEXT_3 }}>{count} Today</Typography>}
      >
        {people === null && (
          <Box role="status" aria-label="Loading" sx={{ border: `1px solid ${LINE}`, borderRadius: '4px' }}>
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  minHeight: 36,
                  px: '12px',
                  ...(i > 0 ? { borderTop: `1px solid ${LINE}` } : {})
                }}
              >
                <Bone width="45%" height={12} radius="4px" />
                <Box sx={{ flex: 1 }} />
                <Bone width={90} height={10} radius="4px" />
              </Box>
            ))}
          </Box>
        )}
        {people !== null && count === 0 && (
          <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3 }}>
            Nobody holds {names.length === 1 ? 'this role' : 'these roles'} yet.
          </Typography>
        )}
        {people !== null && count > 0 && (
          <Box sx={{ border: `1px solid ${LINE}`, borderRadius: '4px' }}>
            {people.map((p, i) => (
              <Box
                key={p.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  minHeight: 36,
                  px: '12px',
                  ...(i > 0 ? { borderTop: `1px solid ${LINE}` } : {})
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontFamily: FONT,
                    fontSize: 13,
                    color: TEXT,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {p.name}
                </Typography>
                <Typography sx={{ ...LABEL, flexShrink: 0, color: TEXT_3 }}>{p.roleName}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </FormRow>
    </FlowStep>
  );
};

export default RoleMembersStep;
